import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MatchCard from '@/components/base/MatchCard';
import ArticleCard from '@/components/base/ArticleCard';
import { SeoMeta, SchemaOrg } from '@/components/base/SeoHead';
import { siteInfo, categories, matches } from '@/mocks/matches';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'football');

  useEffect(() => {
    if (slug) setActiveTab(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab]);

  const category = useMemo(
    () => categories.find((c) => c.slug === activeTab),
    [activeTab]
  );

  const categoryMatches = useMemo(
    () => matches.filter((m) => m.category === activeTab),
    [activeTab]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === activeTab),
    [activeTab]
  );

  const categoryName = category?.name || '赛事竞猜';
  const categoryDesc = category?.description || '';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${siteInfo.name} - ${categoryName}`,
    description: categoryDesc,
    url: `${siteInfo.url}/category/${activeTab}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryMatches.map((m, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'SportsEvent',
          name: `${m.homeTeam} vs ${m.awayTeam}`,
          startDate: m.startTime,
        },
      })),
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `${siteInfo.url}/category/${activeTab}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SeoMeta
        title={`${categoryName} - ${siteInfo.name}`}
        description={categoryDesc}
        keywords={`真人体育${categoryName},${categoryName}竞猜,${categoryName}实时赔率,${siteInfo.keywords}`}
        canonicalUrl={`${siteInfo.url}/category/${activeTab}`}
      />
      <SchemaOrg schema={categorySchema} />
      <SchemaOrg schema={breadcrumbSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryName },
              ]}
            />

            <div className="py-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl">{categoryDesc}</p>
            </div>

            <div className="flex items-center gap-2 pb-6 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 no-underline ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              可竞猜赛事
              <span className="text-sm font-normal text-foreground-500 ml-2">共{categoryMatches.length}场</span>
            </h2>

            {categoryMatches.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryMatches.map((match) => (
                  <MatchCard key={match.id} match={match} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 text-foreground-500">
                <i className="ri-inbox-line text-4xl mb-3 block"></i>
                <p className="text-sm">暂无相关赛事</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-10 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              相关资讯
            </h2>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 text-foreground-500">
                <i className="ri-article-line text-4xl mb-3 block"></i>
                <p className="text-sm">暂无相关资讯</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}